import React from 'react';
import { Kid, Reward } from '../types';

interface RewardsViewProps {
  kid: Kid | undefined;
  sharedStars: number;
  rewards: Reward[];
  onOpenRedeemModal: (reward: Reward) => void;
}

export const RewardsView: React.FC<RewardsViewProps> = ({
  kid,
  sharedStars,
  rewards,
  onOpenRedeemModal,
}) => {
  const kidName = kid?.name || 'Kid';
  const myStars = kid?.personal_stars_available || 0;

  // Personal rewards for this kid
  const personalRewards = rewards.filter(
    (r) => r.is_shared === 0 && (!r.kid_id || r.kid_id === kid?.id)
  );

  // Shared family rewards
  const sharedRewards = rewards.filter((r) => r.is_shared === 1);

  const readyPersonalCount = personalRewards.filter((r) => (r.stars_progress ?? myStars) >= r.star_cost).length;
  const readySharedCount = sharedRewards.filter((r) => (r.stars_progress ?? sharedStars) >= r.star_cost).length;
  const totalReadyCount = readyPersonalCount + readySharedCount;

  return (
    <div className="rewards-view">
      {/* Ready Rewards Guidance Banner */}
      {totalReadyCount > 0 && (
        <div
          id="rewards-ready-guidance-banner"
          style={{
            marginBottom: '20px',
            padding: '14px 18px',
            background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
            border: '2px solid #34d399',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 4px 14px rgba(16, 185, 129, 0.15)',
          }}
        >
          <span style={{ fontSize: '28px' }}>🎉</span>
          <div>
            <h4 style={{ fontSize: '15px', fontWeight: 800, color: '#065f46', margin: 0 }}>
              {totalReadyCount === 1
                ? 'Your reward is ready to collect!'
                : `${totalReadyCount} rewards are ready to collect!`}
            </h4>
            <p style={{ fontSize: '13px', color: '#047857', margin: '2px 0 0', fontWeight: 600 }}>
              Tap "Redeem! 🎁" below and ask an adult to enter their PIN to claim your prize!
            </p>
          </div>
        </div>
      )}

      {/* Star Balance Cards */}
      <div className="rewards-balance-grid">
        <div className="balance-card">
          <p className="balance-card-label">My Stars</p>
          <div className="balance-card-value">
            <span>⭐</span>
            <span id="label-my-stars-count">{myStars}</span>
          </div>
        </div>

        <div className="balance-card shared">
          <p className="balance-card-label">Shared Stars</p>
          <div className="balance-card-value">
            <span>⭐</span>
            <span id="label-shared-stars-count">{sharedStars}</span>
          </div>
        </div>
      </div>

      {/* Kid's Personal Rewards Section */}
      <div className="rewards-section">
        <h3 className="section-title">{kidName}'s Rewards</h3>

        {personalRewards.length === 0 ? (
          <p style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '20px' }}>
            No personal rewards yet! Ask an adult to add some in the Adult Zone.
          </p>
        ) : (
          personalRewards.map((reward) => {
            const cost = reward.star_cost;
            const progress = reward.stars_progress ?? myStars;
            const percent = Math.min(100, Math.round((progress / cost) * 100));
            const isReady = progress >= cost;

            return (
              <div key={reward.id} className="reward-card" id={`reward-card-${reward.id}`}>
                <div className="reward-card-header">
                  <div className="reward-card-info">
                    <span className="reward-card-emoji">{reward.emoji}</span>
                    <div>
                      <h4 className="reward-card-title">{reward.name}</h4>
                      {reward.description && (
                        <p className="reward-card-desc">{reward.description}</p>
                      )}
                    </div>
                  </div>
                  <span className="reward-card-cost">
                    <span>⭐</span>
                    <span>{reward.star_cost}</span>
                  </span>
                </div>

                <div className="reward-card-bar" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
                  <div
                    className="reward-card-fill"
                    style={{ width: `${percent}%` }}
                  />
                </div>

                <div className="reward-card-footer">
                  <span className="reward-card-progress-text">
                    {progress} / {cost} ⭐
                  </span>

                  {isReady ? (
                    <button
                      id={`btn-redeem-${reward.id}`}
                      className="redeem-btn"
                      onClick={() => onOpenRedeemModal(reward)}
                    >
                      Redeem! 🎁
                    </button>
                  ) : (
                    <span className="reward-card-status-text">Keep going! 💪</span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Shared Family Rewards Section */}
      <div className="rewards-section" style={{ marginTop: '24px' }}>
        <h3 className="section-title">Shared Rewards 🤝</h3>

        {sharedRewards.length === 0 ? (
          <p style={{ color: '#94a3b8', fontSize: '14px' }}>
            No shared rewards yet! Add family rewards in the Adult Zone.
          </p>
        ) : (
          sharedRewards.map((reward) => {
            const cost = reward.star_cost;
            const progress = reward.stars_progress ?? sharedStars;
            const percent = Math.min(100, Math.round((progress / cost) * 100));
            const isReady = progress >= cost;

            return (
              <div key={reward.id} className="reward-card" id={`reward-card-${reward.id}`}>
                <div className="reward-card-header">
                  <div className="reward-card-info">
                    <span className="reward-card-emoji">{reward.emoji}</span>
                    <div>
                      <h4 className="reward-card-title">{reward.name}</h4>
                      {reward.description && (
                        <p className="reward-card-desc">{reward.description}</p>
                      )}
                    </div>
                  </div>
                  <span className="reward-card-cost">
                    <span>⭐</span>
                    <span>{reward.star_cost}</span>
                  </span>
                </div>

                <div className="reward-card-bar" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
                  <div
                    className="reward-card-fill"
                    style={{ width: `${percent}%` }}
                  />
                </div>

                <div className="reward-card-footer">
                  <span className="reward-card-progress-text">
                    {progress} / {cost} ⭐
                  </span>

                  {isReady ? (
                    <button
                      id={`btn-redeem-${reward.id}`}
                      className="redeem-btn"
                      onClick={() => onOpenRedeemModal(reward)}
                    >
                      Redeem! 🎁
                    </button>
                  ) : (
                    <span className="reward-card-status-text">Keep going! 💪</span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
